/**
 * src/api/incidentApi.js
 *
 * Thin client for the incidents-write Azure Function — the dedicated
 * per-record write path for confirmed incidents and IR exercises (ADR-0007,
 * docs/decisions/0007-incidents-per-record-storage.md, INA-03). Mirrors
 * bcdrApi.js's / assetApi.js's shape: one wrapper per action, each returning
 * the function's response body so callers can reconcile local state without a
 * full reload.
 *
 * NOT the proposals client. Machine-proposed incident CANDIDATES are accepted
 * and dismissed through src/api/incidentProposalApi.js against a completely
 * separate store (INC-B-002); nothing here touches them.
 *
 * `options` (e.g. `{ keepalive: true }`) passes straight through to azurePost.
 */

import { azurePost, getTenantId } from './azureClient';

async function callIncidentsWrite(action, body, tenantId, options = {}) {
  return azurePost(
    `/api/incidents-write?action=${encodeURIComponent(action)}`,
    { ...body, tenantId },
    options
  );
}

/**
 * Create-or-update one incident by incident.id. Returns { ok, incident }, the
 * incident carrying the `rowVersion` the next save must be based on.
 *
 * The record's own `rowVersion` (the annotation state-read attached when it
 * was loaded) is what the server compares against: a save based on a version
 * another tab has already replaced is refused with 409 rather than silently
 * overwriting it — issue #4846's two-tabs-same-incident case.
 */
export async function saveIncidentRecord(incident, tenantId, options) {
  return callIncidentsWrite('save-incident', { incident }, tenantId, options);
}

/** Delete one incident by id. Returns { ok }. */
export async function deleteIncidentRecord(incidentId, tenantId, options) {
  return callIncidentsWrite('delete-incident', { incidentId }, tenantId, options);
}

/** Create-or-update one IR exercise by exercise.id. Returns { ok, exercise }. */
export async function saveIncidentExerciseRecord(exercise, tenantId, options) {
  return callIncidentsWrite('save-exercise', { exercise }, tenantId, options);
}

/** Delete one IR exercise by id. Returns { ok }. */
export async function deleteIncidentExerciseRecord(exerciseId, tenantId, options) {
  return callIncidentsWrite('delete-exercise', { exerciseId }, tenantId, options);
}

// ── Regulatory reporting actions ────────────────────────────────────────────
//
// Each of these commits the incident server-side and answers with the record
// as committed (carrying its new rowVersion). The caller adopts that record —
// it must not re-save it, which would be a second write based on the version
// the action has just replaced (GH #7017). They go through azurePost like every
// other call so the session's JWT is attached; the panels used to build their
// own fetch with `session.token`, which the session object has never carried,
// and every one of them answered 401. `tenantId` is read only for a
// super-admin caller (resolveTenantId, AUTH-B-002).

/** Run the regulatory classifier on one incident. Returns { incident }. */
export async function classifyIncident(incidentId) {
  return azurePost(`/api/incidents-classify?incidentId=${encodeURIComponent(incidentId)}`, {
    tenantId: getTenantId(),
  });
}

/** Cross-framework consistency check. Returns { passed, issues, incident }. */
export async function checkIncidentConsistency(incidentId) {
  return azurePost('/api/incidents-consistency-check', { incidentId, tenantId: getTenantId() });
}

/** Generate a report draft for one framework. Returns { incident }. */
export async function generateIncidentReportDraft(incidentId, frameworkKey) {
  return azurePost('/api/incidents-report-generate', {
    incidentId,
    frameworkKey,
    tenantId: getTenantId(),
  });
}

/** Build a report package for download. Returns the package plus { incident }. */
export async function exportIncidentReport(incidentId, frameworkKey) {
  return azurePost('/api/incidents-report-export', {
    incidentId,
    frameworkKey,
    tenantId: getTenantId(),
  });
}

// ── Re-classification record (msr 4.6b, criteria-p4 §3.4 rule 8) ───────────
//
// A regime re-classified as not notifiable after its deadline leaves IRR-03
// only through an approved record. Each call answers { incident, request },
// the incident as committed; the caller adopts it (GH #7017).

/** Ask a named approver to approve a re-classification. */
export async function requestIncidentReclassification({
  incidentId,
  regime,
  reason,
  evidence,
  approverId,
  rowVersion,
}) {
  return callIncidentsWrite(
    'request-reclassification',
    { incidentId, regime, reason, evidence, approverId, rowVersion },
    getTenantId()
  );
}

/** The named approver approves or rejects (a rejection needs a note). */
export async function decideIncidentReclassification({
  incidentId,
  regime,
  requestId,
  outcome,
  note,
  rowVersion,
}) {
  return callIncidentsWrite(
    'decide-reclassification',
    { incidentId, regime, requestId, outcome, note, rowVersion },
    getTenantId()
  );
}

/** The requester withdraws a pending request. */
export async function withdrawIncidentReclassification({
  incidentId,
  regime,
  requestId,
  rowVersion,
}) {
  return callIncidentsWrite(
    'withdraw-reclassification',
    { incidentId, regime, requestId, rowVersion },
    getTenantId()
  );
}
